import React, { useState, useRef } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useGeoJSONCenter from './hooks/useGeoJSONCenter';
import useInitializeMap from './hooks/useInitializeMap';
import useMapSaveHandler from './hooks/useMapSaveHandler';
import MapView from './MapView';
import EnhancedMapEditor from './components/MobileMapLayerComponent';

const DEFAULT_CENTER = { lat: 9.5937, lng: 78.9629 };

const MapModal = ({
  defaultValue,
  showModal,
  setShowModal,
  onLocationSelect,
  mode = 'location',
  isMapEditable = true,
  uploadedGeoJSONLayer
}) => {
  const apiKey = process.env.REACT_APP_OLA_API_KEY;

  // Refs
  const mapRef = useRef(null);
  const featureGroupRef = useRef(null);

  // State
  const [userLocation, setUserLocation] = useState(DEFAULT_CENTER);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [addressDetails, setAddressDetails] = useState(null);
  const [polygonData, setPolygonData] = useState(null);
  const [showLocationError, setShowLocationError] = useState(false);
  const [hasLayer, setHasLayer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [areaAcres, setAreaAcres] = useState(0);
  const [areaHectares, setAreaHectares] = useState(0);
  const [perimeter, setPerimeter] = useState(0);
  const [initializing, setInitializing] = useState(true);
  const [locationPermission, setLocationPermission] = useState('prompt');

  const getGeoJSONCenter = useGeoJSONCenter();

  // Initialize the map
  useInitializeMap({
    showModal,
    uploadedGeoJSONLayer,
    defaultValue,
    selectedLocation,
    setInitializing,
    setIsLoading,
    setUserLocation,
    setSelectedLocation,
    setAddressDetails,
    setShowLocationError,
    getGeoJSONCenter,
    apiKey
  });

  const closeModal = () => {
    setShowModal(false);
    setPolygonData(null);
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    setHasLayer(false);
  };

  const handleSearchSelect = async location => {
    try {
      setSelectedLocation(location);
      setUserLocation(location);

      if (mapRef.current?.target) {
        mapRef.current.target.setView([location.lat, location.lng], 20);
      }

      if (mode === 'location') {
        setAddressDetails(location);
      }
    } catch (error) {
      console.error('Search select error:', error);
    }
  };

  const handleSave = useMapSaveHandler({
    mode,
    selectedLocation,
    polygonData,
    onLocationSelect,
    closeModal
  });

  return (
    <Modal show={showModal} onHide={closeModal} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'location' ? 'Select Location' : 'Draw Area'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {showLocationError && (
          <Alert variant="warning" className="mb-3">
            {locationPermission === 'denied'
              ? 'Location access denied. Please enable permissions in your browser settings.'
              : 'Unable to get your current location. Using default location.'}
          </Alert>
        )}

        <div style={{ height: '100%', position: 'relative' }}>
          {initializing ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '70vh' }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading map...</span>
              </Spinner>
            </div>
          ) : (
            <>
              {/* Desktop Map */}
              <div className="d-none d-md-block">
                <MapView
                  mode={mode}
                  userLocation={userLocation}
                  selectedLocation={selectedLocation}
                  setSelectedLocation={setSelectedLocation}
                  setAddressDetails={setAddressDetails}
                  handleSearchSelect={handleSearchSelect}
                  isMapEditable={isMapEditable}
                  uploadedGeoJSONLayer={uploadedGeoJSONLayer}
                  featureGroupRef={featureGroupRef}
                  hasLayer={hasLayer}
                  setHasLayer={setHasLayer}
                  setPolygonData={setPolygonData}
                  setAreaHectares={setAreaHectares}
                  setAreaAcres={setAreaAcres}
                  setPerimeter={setPerimeter}
                  areaAcres={areaAcres}
                  areaHectares={areaHectares}
                  perimeter={perimeter}
                />
              </div>

              {/* Mobile Map */}
              <div className="d-block d-md-none">
                <EnhancedMapEditor
                  userLocation={userLocation}
                  uploadedGeoJSONLayer={uploadedGeoJSONLayer}
                  onPolygonChange={setPolygonData}
                  setAreaHectares={setAreaHectares}
                  setAreaAcres={setAreaAcres}
                  setSelectedLocation={setSelectedLocation}
                  isMapEditable={isMapEditable}
                  handleSearchSelect={handleSearchSelect}
                  selectedLocation={selectedLocation}
                  mainMode={mode}
                  addressDetails={addressDetails}
                />
              </div>
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        {isMapEditable && (
          <>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={(mode === 'location' && !selectedLocation) || isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    size="sm"
                    animation="border"
                    role="status"
                  />
                  <span className="ms-2">Loading...</span>
                </>
              ) : (
                `Save ${mode === 'location' ? 'Location' : 'Area'}`
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

MapModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['location', 'polygon']).isRequired,
  isMapEditable: PropTypes.bool,
  uploadedGeoJSONLayer: PropTypes.object,
  defaultValue: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    accuracy: PropTypes.number
  })
};

export default MapModal;
